/** Lazy child-run grid shown in a parent row's master-detail panel.
 *
 * Fetches `?parent=<id>` only when the panel is expanded (mounting the
 * component IS the trigger — the delivery-logs summary panel convention).
 * Children come back `created_ts` ascending; a group rarely exceeds a few
 * dozen runs, so one page of 200 covers it (the count line says otherwise
 * when it doesn't).
 */
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as importRunsApi from '@/api/importRuns';

import { buildColumns, type BuildColumnsOptions } from './importRunsColumns';

const CHILD_PAGE_SIZE = 200;

/** Child-grid column subset — group/child-count/url add nothing under a parent. */
const CHILD_FIELDS = new Set([
  'open', 'created_ts', 'state', 'origin', 'sources', 'processing_duration', 'id', 'request_id',
]);

export interface ImportRunChildrenProps {
  parentId: string;
  onQuickFilter: BuildColumnsOptions['onQuickFilter'];
  detailPath: BuildColumnsOptions['detailPath'];
}

export function ImportRunChildren({ parentId, onQuickFilter, detailPath }: ImportRunChildrenProps) {
  const query = useQuery({
    queryKey: ['import-runs', 'children', parentId],
    queryFn: () => importRunsApi.list({ parent: parentId, page_size: CHILD_PAGE_SIZE }),
  });

  const columns = useMemo(
    () =>
      buildColumns({ showTenant: false, onQuickFilter, detailPath }).filter((c) =>
        CHILD_FIELDS.has(c.field),
      ),
    [onQuickFilter, detailPath],
  );

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return <Alert severity="error" sx={{ m: 1.5 }}>Couldn’t load the child imports.</Alert>;
  }

  const { results, count } = query.data;
  return (
    <Box sx={{ p: 1.5, bgcolor: 'action.hover' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {count.toLocaleString()} child import{count === 1 ? '' : 's'}
        {count > results.length ? ` (showing first ${results.length})` : ''}
      </Typography>
      <DataGridPro
        rows={results}
        getRowId={(r) => r.id}
        columns={columns}
        density="compact"
        autoHeight
        hideFooter
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        sortingMode="server"
        sx={{ bgcolor: 'background.paper' }}
      />
    </Box>
  );
}
